import { PayloadAction } from '@reduxjs/toolkit';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import {
    actionTypes,
    IEmissionsOfEcaSeaAreas,
    IEmissionsOfOperationalPhases,
    IFleetCiiAlignment,
    IGreenSummary,
    IMsiEnvironmentalCredentials,
    IMsiSingleEnvironmentalCredentials,
    IRollingAerValues,
    IVesselCiiAlignments,
    IVesselKeyMetricsDateBased,
    IVesselsEtsAndFuelEuEmissions,
    IVesselsMainEngineFuelConsumes
} from './greenTypes';
import {
    getCohortEmissionsOfEcaSeaAreasByImoNumbers,
    getCohortEmissionsOfOperationalPhasesByImoNumbers,
    getEmissionsOfEcaSeaAreasByImoNumber,
    getEmissionsOfOperationalPhasesByImoNumber,
    getFleetCiiAlignmentByImoNumbers,
    getVesselsEtsAndFuelEuEmissionsByImoNumbers,
    getVesselsMainEngineFuelConsumesByImoNumbers,
    getGreenSummaryByImoNumbers,
    getMsiEnvironmentalCredentialsByImoNumber,
    getRollingAerValuesByImoNumber,
    getVesselCiiAlignmentsByImoNumber,
    getVesselKeyMetricsDateBasedByImoNumbers
} from './greenApi';
import {
    greenActions,
    selectMsiEnvironmentalCredentialsDatas
} from './greenSlice';
import { handleAxiosError } from '../../utils/helpers';
import { 
    trackEvent,
    trackExceptionWithContext, 
    LogLevel 
} from '../../services/application-insights/appInsights';
import { 
    AppInsightsActions, 
    AppInsightsComponents, 
    AppInsightsErrorTypes 
} from '../../services/application-insights/appInsightsConstants';

function* getMsiEnvironmentalCredentialsByImoNumberSaga({ payload: imoNumbers }: PayloadAction<number[]>) {
    const requestId = `green-env-credentials-${Date.now()}`;
    
    try {
        // Track the attempt
        trackEvent(AppInsightsActions.GREEN_ENVIRONMENTAL_CREDENTIALS_LOAD, {
            requestId,
            imoNumbers: imoNumbers.join(','),
            imoCount: imoNumbers.length.toString(),
            component: AppInsightsComponents.GREEN_COMPONENT
        });

        const msiEnvironmentalCredentialsDatas: IMsiSingleEnvironmentalCredentials[] = yield select(selectMsiEnvironmentalCredentialsDatas);

        // Check if data exists for single imo number
        if (imoNumbers.length === 1) {
            const existing = msiEnvironmentalCredentialsDatas && msiEnvironmentalCredentialsDatas.find((data: IMsiSingleEnvironmentalCredentials) => data.imoNumber === imoNumbers[0]);
            if (existing) {
                trackEvent(AppInsightsActions.GREEN_DATA_FETCH_SUCCESS, {
                    requestId,
                    dataSource: 'cache',
                    imoNumber: imoNumbers[0].toString(),
                    component: AppInsightsComponents.GREEN_COMPONENT
                });
                yield put(greenActions.selectSingleMsiEnvironmentalCredentials(existing));
                return;
            }
        }

        const response: IMsiEnvironmentalCredentials = yield call(getMsiEnvironmentalCredentialsByImoNumber, imoNumbers);
        
        trackEvent(AppInsightsActions.GREEN_DATA_FETCH_SUCCESS, {
            requestId,
            dataSource: 'api',
            credentialsCount: response.singleEnvironmentalCredentials?.length?.toString() || '0',
            component: AppInsightsComponents.GREEN_COMPONENT
        });
        
        yield put(greenActions.getMsiEnvironmentalCredentialsSuccessAction(response));

    } catch (error: any) {
        trackExceptionWithContext(error, {
            component: AppInsightsComponents.GREEN_COMPONENT,
            action: AppInsightsActions.GREEN_DATA_FETCH_FAILURE,
            severity: LogLevel.Error,
            additionalData: {
                requestId,
                imoNumbers: imoNumbers.join(','),
                errorType: AppInsightsErrorTypes.GREEN_DATA_ERROR,
                errorMessage: error.message || 'Unknown error'
            }
        });
        
        yield put(greenActions.getMsiEnvironmentalCredentialsErrorAction(handleAxiosError(error)));
    }
}

function* getGreenSummaryByImoNumbersSaga({ payload: { imoNumbers, startYearMonth, endYearMonth } }: PayloadAction<{ imoNumbers: number[], startYearMonth: number, endYearMonth: number }>) {
    const requestId = `green-summary-${Date.now()}`;
    
    try {
        trackEvent(AppInsightsActions.GREEN_DATA_FETCH_ATTEMPT, {
            requestId,
            action: 'green-summary-load',
            imoNumbers: imoNumbers.join(','),
            dateRange: `${startYearMonth}-${endYearMonth}`,
            component: AppInsightsComponents.GREEN_COMPONENT
        });

        const response: IGreenSummary[] = yield call(getGreenSummaryByImoNumbers, imoNumbers, startYearMonth, endYearMonth);
        
        trackEvent(AppInsightsActions.GREEN_DATA_FETCH_SUCCESS, {
            requestId,
            action: 'green-summary-load',
            resultCount: response.length.toString(),
            component: AppInsightsComponents.GREEN_COMPONENT
        });
        
        yield put(greenActions.getGreenSummarySuccessAction(response));

    } catch (error: any) {
        trackExceptionWithContext(error, {
            component: AppInsightsComponents.GREEN_COMPONENT,
            action: AppInsightsActions.GREEN_DATA_FETCH_FAILURE,
            severity: LogLevel.Error,
            additionalData: {
                requestId,
                action: 'green-summary-load',
                imoNumbers: imoNumbers.join(','),
                errorType: AppInsightsErrorTypes.GREEN_DATA_ERROR
            }
        });
        
        yield put(greenActions.getGreenSummaryErrorAction(handleAxiosError(error)));
    }
}

function* getRollingAerValuesByImoNumberSaga({ payload: { imoNumber, startDate, endDate } }: PayloadAction<{ imoNumber: number, startDate: string, endDate: string }>) {
    const requestId = `rolling-aer-${Date.now()}`;
    
    try {
        trackEvent(AppInsightsActions.GREEN_ROLLING_AER_VALUES_LOAD, {
            requestId,
            imoNumber: imoNumber.toString(),
            dateRange: `${startDate}_to_${endDate}`,
            component: AppInsightsComponents.GREEN_COMPONENT
        });

        const response: IRollingAerValues[] = yield call(getRollingAerValuesByImoNumber, imoNumber, startDate, endDate);

        trackEvent(AppInsightsActions.GREEN_DATA_FETCH_SUCCESS, {
            requestId,
            action: 'rolling-aer-values',
            resultCount: response.length.toString(),
            component: AppInsightsComponents.GREEN_COMPONENT
        });

        yield put(greenActions.getRollingAerValuesSuccessAction(response));

    } catch (error: any) {
        trackExceptionWithContext(error, {
            component: AppInsightsComponents.GREEN_COMPONENT,
            action: AppInsightsActions.GREEN_DATA_FETCH_FAILURE,
            severity: LogLevel.Error,
            additionalData: {
                requestId,
                action: 'rolling-aer-values',
                imoNumber: imoNumber.toString(),
                errorType: AppInsightsErrorTypes.GREEN_CALCULATION_ERROR
            }
        });
        
        yield put(greenActions.getRollingAerValuesErrorAction(handleAxiosError(error)));
    }
}

function* getEmissionsOfEcaSeaAreasByImoNumbersSaga({ payload: { imoNumber, startYearMonth, endYearMonth }, }: PayloadAction<{ imoNumber: number, startYearMonth: number, endYearMonth: number }>) {
    try {
        const response: IEmissionsOfEcaSeaAreas[] = yield call(getEmissionsOfEcaSeaAreasByImoNumber, imoNumber, startYearMonth, endYearMonth);
        yield put(greenActions.getEmissionsOfEcaSeaAreasSuccessAction(response));

    } catch (error: any) {
        yield put(greenActions.getEmissionsOfEcaSeaAreasErrorAction(handleAxiosError(error)));
    }
}

function* getCohortEmissionsOfEcaSeaAreasByImoNumbersSaga({ payload: { imoNumbers, startYearMonth, endYearMonth } }: PayloadAction<{ imoNumbers: number[], startYearMonth: number, endYearMonth: number }>) {
    try {
        const response: IEmissionsOfEcaSeaAreas[] = yield call(getCohortEmissionsOfEcaSeaAreasByImoNumbers, imoNumbers, startYearMonth, endYearMonth);
        yield put(greenActions.getCohortEmissionsOfEcaSeaAreasSuccessAction(response));

    } catch (error: any) {
        yield put(greenActions.getCohortEmissionsOfEcaSeaAreasErrorAction(handleAxiosError(error)));
    }
}

function* getEmissionsOfOperationalPhasesByImoNumberSaga({ payload: { imoNumber, startYearMonth, endYearMonth } }: PayloadAction<{ imoNumber: number, startYearMonth: number, endYearMonth: number }>) {
    try {
        const response: IEmissionsOfOperationalPhases[] = yield call(getEmissionsOfOperationalPhasesByImoNumber, imoNumber, startYearMonth, endYearMonth);
        yield put(greenActions.getEmissionsOfOperationalPhasesSuccessAction(response));

    } catch (error: any) {
        yield put(greenActions.getEmissionsOfOperationalPhasesErrorAction(handleAxiosError(error)));
    }
}

function* getCohortEmissionsOfOperationalPhasesByImoNumbersSaga({ payload: { imoNumbers, startYearMonth, endYearMonth } }: PayloadAction<{ imoNumbers: number[], startYearMonth: number, endYearMonth: number }>) {
    try {
        const response: IEmissionsOfOperationalPhases[] = yield call(getCohortEmissionsOfOperationalPhasesByImoNumbers, imoNumbers, startYearMonth, endYearMonth);
        yield put(greenActions.getCohortEmissionsOfOperationalPhasesSuccessAction(response));

    } catch (error: any) {
        yield put(greenActions.getCohortEmissionsOfOperationalPhasesErrorAction(handleAxiosError(error)));
    }
}

function* getVesselCiiAlignmentsByImoNumberSaga({ payload: { imoNumber, startYearMonth, endYearMonth } }: PayloadAction<{ imoNumber: number, startYearMonth: number, endYearMonth: number }>) {
    try {
        const response: IVesselCiiAlignments[] = yield call(getVesselCiiAlignmentsByImoNumber, imoNumber, startYearMonth, endYearMonth);
        yield put(greenActions.getVesselCiiAlignmentsSuccessAction(response));

    } catch (error: any) {
        yield put(greenActions.getVesselCiiAlignmentsErrorAction(handleAxiosError(error)));
    }
}

function* getFleetCiiAlignmentByImoNumberSaga({ payload: { imoNumbers, startYearMonth, endYearMonth } }: PayloadAction<{ imoNumbers: number[], startYearMonth: number, endYearMonth: number }>) {
    try {
        const response: IFleetCiiAlignment = yield call(getFleetCiiAlignmentByImoNumbers, imoNumbers, startYearMonth, endYearMonth);
        yield put(greenActions.getFleetCiiAlignmentSuccessAction(response));

    } catch (error: any) {
        yield put(greenActions.getFleetCiiAlignmentPhasesErrorAction(handleAxiosError(error)));
    }
}

function* getVesselsEtsAndFuelEuEmissionsByImoNumbersSaga({ payload: { imoNumbers, startYearMonth, endYearMonth } }: PayloadAction<{ imoNumbers: number[], startYearMonth: number, endYearMonth: number }>) {
    try {
        const response: IVesselsEtsAndFuelEuEmissions[] = yield call(getVesselsEtsAndFuelEuEmissionsByImoNumbers, imoNumbers, startYearMonth, endYearMonth);
        yield put(greenActions.getVesselsEtsAndFuelEuEmissionsSuccessAction(response));

    } catch (error: any) {
        yield put(greenActions.getVesselsEtsAndFuelEuEmissionsErrorAction(handleAxiosError(error)));
    }
}

function* getCohortVesselsEtsAndFuelEuEmissionsByImoNumbersSaga({ payload: { imoNumbers, startYearMonth, endYearMonth } }: PayloadAction<{ imoNumbers: number[], startYearMonth: number, endYearMonth: number }>) {
    try {
        const response: IVesselsEtsAndFuelEuEmissions[] = yield call(getVesselsEtsAndFuelEuEmissionsByImoNumbers, imoNumbers, startYearMonth, endYearMonth);
        yield put(greenActions.getCohortVesselsEtsAndFuelEuEmissionsSuccessAction(response));

    } catch (error: any) {
        yield put(greenActions.getCohortVesselsEtsAndFuelEuEmissionsErrorAction(handleAxiosError(error)));
    }
}

function* getVesselsMainEngineFuelConsumesByImoNumbersSaga({ payload: { imoNumbers, startYearMonth, endYearMonth } }: PayloadAction<{ imoNumbers: number[], startYearMonth: number, endYearMonth: number }>) {
    try {
        const response: IVesselsMainEngineFuelConsumes[] = yield call(getVesselsMainEngineFuelConsumesByImoNumbers, imoNumbers, startYearMonth, endYearMonth);
        yield put(greenActions.getVesselsMainEngineFuelConsumesSuccessAction(response));

    } catch (error: any) {
        yield put(greenActions.getVesselsMainEngineFuelConsumesErrorAction(handleAxiosError(error)));
    }
}

function* getCohortVesselsMainEngineFuelConsumesByImoNumbersSaga({ payload: { imoNumbers, startYearMonth, endYearMonth } }: PayloadAction<{ imoNumbers: number[], startYearMonth: number, endYearMonth: number }>) {
    try {
        const response: IVesselsMainEngineFuelConsumes[] = yield call(getVesselsMainEngineFuelConsumesByImoNumbers, imoNumbers, startYearMonth, endYearMonth);
        yield put(greenActions.getCohortVesselsMainEngineFuelConsumesSuccessAction(response));

    } catch (error: any) {
        yield put(greenActions.getCohortVesselsMainEngineFuelConsumesErrorAction(handleAxiosError(error)));
    }
}

function* getVesselKeyMetricsDateBasedByImoNumbersSaga({ payload: { imoNumbers, startYearMonth, endYearMonth } }: PayloadAction<{ imoNumbers: number[], startYearMonth: number, endYearMonth: number }>) {
    try {
        const response: IVesselKeyMetricsDateBased[] = yield call(getVesselKeyMetricsDateBasedByImoNumbers, imoNumbers, startYearMonth, endYearMonth);
        yield put(greenActions.getVesselKeyMetricsDateBasedSuccessAction(response));

    } catch (error: any) {
        yield put(greenActions.getVesselKeyMetricsDateBasedErrorAction(handleAxiosError(error)));
    }
}

function* getCohortVesselKeyMetricsDateBasedByImoNumbersSaga({ payload: { imoNumbers, startYearMonth, endYearMonth } }: PayloadAction<{ imoNumbers: number[], startYearMonth: number, endYearMonth: number }>) {
    try {
        const response: IVesselKeyMetricsDateBased[] = yield call(getVesselKeyMetricsDateBasedByImoNumbers, imoNumbers, startYearMonth, endYearMonth);
        yield put(greenActions.getCohortVesselKeyMetricsDateBasedSuccessAction(response));

    } catch (error: any) {
        yield put(greenActions.getCohortVesselKeyMetricsDateBasedErrorAction(handleAxiosError(error)));
    }
}

export function* watchGreenActions(): Generator {
    yield takeLatest(actionTypes.GET_MSI_ENVIROMENTAL_CREDENTIALS, getMsiEnvironmentalCredentialsByImoNumberSaga);
    yield takeLatest(actionTypes.GET_GREEN_SUMMARY, getGreenSummaryByImoNumbersSaga);
    yield takeLatest(actionTypes.GET_ROLLING_AER_VALUES, getRollingAerValuesByImoNumberSaga);
    yield takeLatest(actionTypes.GET_EMISSIONS_OF_ECA_SEA_AREAS, getEmissionsOfEcaSeaAreasByImoNumbersSaga);
    yield takeLatest(actionTypes.GET_COHORT_EMISSIONS_OF_ECA_SEA_AREAS, getCohortEmissionsOfEcaSeaAreasByImoNumbersSaga);
    yield takeLatest(actionTypes.GET_EMISSIONS_OF_OPERATIONAL_PHASES, getEmissionsOfOperationalPhasesByImoNumberSaga);
    yield takeLatest(actionTypes.GET_COHORT_EMISSIONS_OF_OPERATIONAL_PHASES, getCohortEmissionsOfOperationalPhasesByImoNumbersSaga);
    yield takeLatest(actionTypes.GET_VESSEL_CII_ALIGNMENTS, getVesselCiiAlignmentsByImoNumberSaga);
    yield takeLatest(actionTypes.GET_FLEET_CII_ALIGNMENT, getFleetCiiAlignmentByImoNumberSaga);
    yield takeLatest(actionTypes.GET_VESSELS_ETS_AND_FUELEU_EMISSIONS, getVesselsEtsAndFuelEuEmissionsByImoNumbersSaga);
    yield takeLatest(actionTypes.GET_COHORT_VESSELS_ETS_AND_FUELEU_EMISSIONS, getCohortVesselsEtsAndFuelEuEmissionsByImoNumbersSaga);
    yield takeLatest(actionTypes.GET_VESSELS_MAIN_ENGINE_FUEL_CONSUMES, getVesselsMainEngineFuelConsumesByImoNumbersSaga);
    yield takeLatest(actionTypes.GET_COHORT_VESSELS_MAIN_ENGINE_FUEL_CONSUMES, getCohortVesselsMainEngineFuelConsumesByImoNumbersSaga);
    yield takeLatest(actionTypes.GET_VESSEL_KEY_METRICS_DATE_BASED, getVesselKeyMetricsDateBasedByImoNumbersSaga);
    yield takeLatest(actionTypes.GET_COHORT_VESSEL_KEY_METRICS_DATE_BASED, getCohortVesselKeyMetricsDateBasedByImoNumbersSaga);
}